import React, { HTMLAttributes, useEffect, useRef, useState } from 'react';
import Button from '../Button/Button';
import Dropdown, { DropdownItem } from '../Dropdown/Dropdown';
import styles from './SplitButton.module.scss';
import { splitButtonIds } from '../../../lib/tests/testIds';
import { useAttributeLabel } from '../../../lib/hooks/labels';
import { ATTRIBUTES } from '../../../lib/constants/attributes';
import { LABEL_KEYS } from '../../../lib/constants/labelKeys';
import { classes } from '../../../lib/utils/cssUtils';

export type SplitButtonProps<T> = {
  options: { label: string; value: T }[];
  onOptionSelect: (value: T) => void;
} & HTMLAttributes<HTMLDivElement>;

const SplitButton = <T extends string>({ options, onOptionSelect, className, ...rest }: SplitButtonProps<T>) => {
  const [activeOption, setActiveOption] = useState(options[0]);
  const arrowAriaLabel = useAttributeLabel(ATTRIBUTES.ARIA_LABEL, LABEL_KEYS.SPLIT_BUTTON_ARROW_ARIA_LABEL);
  const valueRef = useRef<HTMLButtonElement>(null);

  const handleOptionSelect = (value: T) => {
    const newValue = options.find(option => option.value === value);
    if (newValue) {
      setActiveOption(newValue);
    }
  };

  useEffect(() => {
    valueRef.current?.focus();
  }, [activeOption]);

  return (
    <div className={classes(styles.splitButtonContainer, className)} {...rest}>
      <Button
        ref={valueRef}
        className={styles.subButton}
        onClick={() => onOptionSelect(activeOption.value)}
        data-testid={splitButtonIds.splitButtonValue}
      >
        {activeOption.label}
      </Button>
      <Dropdown<T>
        data-testid={splitButtonIds.splitButtonDropdown}
        variant="secondary"
        value={activeOption.value}
        onChange={event => handleOptionSelect(event.target.value as T)}
        classes={{ select: styles.selectInput, icon: styles.icon, iconOpen: styles.iconOpened }}
        renderValue={() => null}
        className={styles.dropdown}
        inputProps={{ ...arrowAriaLabel }}
      >
        {options?.map(({ label, value }) => (
          <DropdownItem key={value} value={value}>
            {label}
          </DropdownItem>
        ))}
      </Dropdown>
    </div>
  );
};

export default SplitButton;
